import {
  DashboardTransactionSummary,
  MatchedPlaidTransaction,
  SpendingBreakdownItem,
  SpendingImpactValueRow,
  currencyFormat,
  User,
} from "@ecountabl/lib"
import React, { useContext, useEffect, useMemo, useState } from "react"
import { StyleSheet, Text, TouchableOpacity, View } from "react-native"
import { ShadowView, textStyles } from "../style"
import Transaction from "./transaction"
import { CommonActions, Link, useNavigation } from "@react-navigation/native"
import type { ParamListBase } from "@react-navigation/native"
import { ThemeContext } from "../utility/context/ThemeContext"
import { MAX_CONTENT_WIDTH } from "../config"
import { Ionicons } from "@expo/vector-icons"
import ThemedDialog from "../style/dialog"
import type { NativeStackNavigationProp } from "@react-navigation/native-stack"
import Bullet from "../style/bullet"
import ValueLabelCard from "../utility/value-label-card"
import { ValueBadge } from "../utility/value-badge"
import { useAppSelector } from "../store"
import api from "../api-client"
import { usePlaidLink } from "../utility/context"
import Haptics from "../utility/haptics"
import { SpendingImpactSkeleton } from "./spending-impact-skeleton"

function userReportsHasAccounts(user: Partial<User> | undefined): boolean {
  return !!user && 'hasAccounts' in user && user.hasAccounts === true
}

const SpendingImpact:React.FC = () => {
  const theme = useContext(ThemeContext).theme
  const { primary, danger, textMain, bgMain } = theme.colors
  const { user } = useAppSelector(state => state.auth)
  const { accounts, institutions } = useAppSelector(state => state.accountsSlice)
  const _iaMap = user?.valuedImpactAreaMap
  const linkedAccountsFromUser = Array.isArray(user?.accounts) && user.accounts.length > 0
  const hasAccounts = accounts.length > 0
    || institutions.length > 0
    || userReportsHasAccounts(user)
    || linkedAccountsFromUser

  const [spendingSummary, setSpendingSummary] = useState({
    transactions: [] as MatchedPlaidTransaction[],
    valuesMatchSpending: 0,
    valuesMatchTransactions: 0,
    spendingBreakdown: {} as Record<string, SpendingBreakdownItem>,
    spendingImpactValueRows: [] as SpendingImpactValueRow[],
  })
  const [loadingSpendingSummary, setLoadingSpendingSummary] = useState(false)
  const [errors, setErrors] = useState<Record<string,{code: number, msg: string}>>({})

  const { startPlaidLinkSession } = usePlaidLink()

  // get dashboard transaction summary
  useEffect(() => {
    const getSummary = async () => {
      setLoadingSpendingSummary(true)
      try {
        const dashboardInfo = await getDashboardTransactionSummary()
        if (!dashboardInfo) return

        setSpendingSummary({
          transactions: dashboardInfo?.transactions ?? [],
          valuesMatchSpending: dashboardInfo?.valuesMatchSpending ?? 0,
          valuesMatchTransactions: dashboardInfo?.valuesMatchTransactions ?? 0,
          spendingBreakdown: dashboardInfo?.spendingBreakdown ?? {},
          spendingImpactValueRows: dashboardInfo?.spendingImpactValueRows ?? [],
        })

        // possible case of error being resolved between retries
        if (errors.spending) {
          setErrors((errorsOld) => {
            delete errorsOld.spending
            return errorsOld
          })
        }
      } finally {
        setLoadingSpendingSummary(false)
      }
    }

    // Fetch on mount and when user value selections change.
    // Do not gate on accounts hydration; the backend can return an empty summary.
    getSummary()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_iaMap, accounts.length, institutions.length])

  const getDashboardTransactionSummary = async ():Promise<DashboardTransactionSummary | null> => {
    try {
      const dashboardInfo = await api.getDashboardTransactionSummary()
      return dashboardInfo
    } catch (ex) {
      if (ex.status === 400 && ex.tryAgain) {
        await new Promise((resolve) => setTimeout(resolve, 3000)) // Wait 3 seconds
        return getDashboardTransactionSummary() // Retry fetching
      } else {
        setErrors((errorsOld) => ({
          ...errorsOld,
          spending: { code: ex.status, msg: ex.message }
        }))
        setLoadingSpendingSummary(false)
        return null
      }
    }
  }

  const getTransactionsErrorMsg = () => {
    if (errors.spending?.code === 401) {
      return "There was an error gathering your spending information. Please close this tab and sign in again for a better experience."
    }
    return errors.spending?.msg || "There was an error gathering your spending information. Please try again later."
  }

  const renderCardContent = () => {
    if (loadingSpendingSummary) {
      return <>
        <SpendingImpactSkeleton recentTransactionsCount={5} />
        <View style={{paddingVertical: 12, alignSelf: "flex-start"}}>
          <ViewAllTransactionsButton />
        </View>
      </>
    } else if (errors.spending) {
      return <>
        <View style={{gap: 10, alignItems: "flex-start", flexDirection: "row"}}>
          <Bullet color={danger} size={6} style={{paddingRight: 6}} />
          <Text style={[textStyles.body, {color: textMain}]}>
            {getTransactionsErrorMsg()}
          </Text>
        </View>
        <View style={{paddingVertical: 12, alignSelf: "flex-start"}}>
          <ViewAllTransactionsButton />
        </View>
      </>
    } else if (!hasAccounts) {
      return <>
        <View style={{gap:10, alignItems: "flex-start"}}>
          <Text style={[textStyles.body, {color: textMain}]}>
            Looks like you don&apos;t have any accounts linked yet
          </Text>
          <TouchableOpacity
            onPress={() => {
              Haptics.impactMedium()
              startPlaidLinkSession({
                redirectPath: "dashboard",
              })
            }}
            hitSlop={10}
          >
            <Text style={[textStyles.body, {color: primary}]}>Link an account</Text>
          </TouchableOpacity>
        </View>
      </>
    }

    return (
      <View style={{gap: 8}}>
        <Text style={[textStyles.body, { color: textMain }]}>
          Vote with your wallet and support the causes that align with your values
        </Text>
        <TransactionBreakdown
          breakdown={spendingSummary.spendingBreakdown}
          valueRows={spendingSummary.spendingImpactValueRows}
          valuesMatchSpending={spendingSummary.valuesMatchSpending}
          valuesMatchTransactions={spendingSummary.valuesMatchTransactions}
        />
        <TransactionSection
          transactions={spendingSummary.transactions}
        />
        <View style={{paddingVertical: 12, alignSelf: "flex-start"}}>
          <ViewAllTransactionsButton />
        </View>
      </View>
    )
  }

  return (
    <View style={{alignSelf: "center", width: "100%", maxWidth: MAX_CONTENT_WIDTH, paddingHorizontal: 16}}>
      <Text style={[textStyles.h2, {marginBottom: 10, color: textMain}]}>
        Your Spending Impact
      </Text>
      <ShadowView style={[styles.card, {backgroundColor: bgMain}]}>
        {renderCardContent()}
      </ShadowView>
    </View>
  )
}

type TransactionsProps = {
  transactions: MatchedPlaidTransaction[];
}

const TransactionSection:React.FC<TransactionsProps> = ({ transactions }: TransactionsProps) => {
  const [companyNavError, setCompanyNavError] = useState(false)

  const theme = useContext(ThemeContext).theme
  const { textMain } = theme.colors

  const navigation = useNavigation<NativeStackNavigationProp<ParamListBase>>()

  return <>
    <View style={{gap: 10}}>
      {
        transactions.length > 0 && (
          <Text style={[textStyles.h3, {color: textMain}]}>
            Most Recent Transactions
          </Text>
        )
      }
      {
        transactions.map((transaction, index) => {
          return (
            <Transaction
              key={index.toString()}
              tx={transaction}
              navigation={navigation}
              onErrorNavigating={() => setCompanyNavError(true)}
            />
          )
        })
      }
    </View>
    {/* company nav error modal */}
    <ThemedDialog
      options={[{
        title: "Okay",
        icon: <Ionicons name="checkmark" size={22}/>,
        role: "confirm",
        onPress: () => setCompanyNavError(false)
      }]}
      visible={companyNavError}
      errorAnimation={companyNavError}
      backDropProps={{onPress: () => setCompanyNavError(false)}}
      title="Error"
      subMessage="An error occurred while retrieving company information. Please try again later."
    />
  </>
}

const formatSelectedValueCount = (count: number, selected: boolean): string => {
  if (selected && count === 0) return "-"
  return count.toLocaleString("en-US")
}

const formatSelectedValueSpending = (spending: number, selected: boolean): string => {
  if (selected && spending === 0) return "-"
  return currencyFormat(spending, { shortenLargeNumbers: true })
}

const legacyRowsFromBreakdown = (
  breakdown: Record<string, SpendingBreakdownItem>
): SpendingImpactValueRow[] => {
  return Object.entries(breakdown)
    .map(([label, item]) => ({
      slug: label,
      label,
      color: item.color,
      icon: null,
      value_selected: true,
      transaction_count: item.count,
      spending: item.total,
    }))
    .sort((a, b) => b.transaction_count - a.transaction_count)
}

type TransactionBreakdownProps = {
  breakdown: Record<string, SpendingBreakdownItem>;
  valueRows: SpendingImpactValueRow[];
  valuesMatchSpending: number;
  valuesMatchTransactions: number;
}

const TransactionBreakdown: React.FC<TransactionBreakdownProps> = ({
  breakdown = {},
  valueRows,
  valuesMatchSpending,
  valuesMatchTransactions,
}: TransactionBreakdownProps) => {
  const theme = useContext(ThemeContext).theme
  const { textMain, textAlt, primary } = theme.colors

  const rows = useMemo(() => {
    const source =
      valueRows && valueRows.length > 0
        ? valueRows
        : legacyRowsFromBreakdown(breakdown)
    return source.filter((row) => row.value_selected !== false)
  }, [valueRows, breakdown])

  const MIN_WIDTH = 150
  const showTable = rows.length > 0
  const tableBorderColor = `${textAlt}55`

  return (
    <View style={{ gap: 10 }}>
      <Text style={[textStyles.h3, { color: textMain }]}>Values-Aligned Totals</Text>
      <View style={{ flexDirection: "column", height: "auto", gap: 10 }}>
        <View style={{ flexDirection: "column", gap: 5 }}>
          <View style={[styles.headerRow]}>
            <ValueLabelCard
              value={currencyFormat(valuesMatchSpending, {
                removeNegatives: true,
                shortenLargeNumbers: true,
              })}
              label="Values-Aligned Spending"
              icon={{ name: "dollar", family: "FontAwesome" }}
              containerStyle={{ flex: 1, minWidth: MIN_WIDTH }}
            />
            <ValueLabelCard
              value={valuesMatchTransactions.toLocaleString("en-US")}
              label="Values-Aligned Transactions"
              icon={{ name: "heart", family: "Foundation" }}
              containerStyle={{ flex: 1, minWidth: MIN_WIDTH }}
            />
          </View>
          {valuesMatchTransactions === 0 ? (
            <Text style={[textStyles.subscript, { color: textMain }]}>
              <Link
                action={CommonActions.navigate({
                  name: "ExploreCompaniesStack",
                  params: {
                    screen: "ExploreCompaniesHome",
                  },
                })}
                href={"/explore-companies"}
                onPress={() => {
                  Haptics.selection()
                }}
              >
                <Text style={[textStyles.h4, { color: primary, paddingVertical: 8 }]}>
                  Find a values-based business
                </Text>
              </Link>
            </Text>
          ) : null}
        </View>

        {showTable ? (
          <View style={{ gap: 8 }}>
            <Text style={[textStyles.subscript, { color: textAlt, lineHeight: 18 }]}>
              Of your values-aligned transactions, this is how that spending breaks down by value.
            </Text>
            <View style={styles.infoRow}>
              <Ionicons name="information-circle-outline" size={18} color={textAlt} />
              <Text style={[textStyles.subscript, { color: textAlt, flex: 1 }]}>
                A single transaction can align with more than one value
              </Text>
            </View>
            <View
              style={[
                styles.breakdownTable,
                { borderColor: tableBorderColor },
              ]}
            >
              <View style={[styles.bdHeaderRow, { borderBottomColor: tableBorderColor }]}>
                <Text style={[styles.bdHeadCell, styles.bdHeadValue, { color: textAlt }]}>
                  Value
                </Text>
                <Text style={[styles.bdHeadCell, styles.bdHeadHash, { color: textAlt }]}>#</Text>
                <Text style={[styles.bdHeadCell, styles.bdHeadDollar, { color: textAlt }]}>$</Text>
              </View>
              {rows.map((row) => {
                const icon = row.icon
                const selected = row.value_selected
                const txFmt = formatSelectedValueCount(row.transaction_count, selected)
                const spendFmt = formatSelectedValueSpending(row.spending, selected)

                return (
                  <View
                    key={row.slug}
                    style={[styles.bdRow, { borderBottomColor: tableBorderColor }]}
                  >
                    <View style={[styles.bdCell, styles.bdCellValue]}>
                      <ValueBadge
                        color={row.color}
                        accentFallbackColor="#64748b"
                        icon={icon}
                        label={row.label}
                        size={28}
                        iconSize={14}
                      />
                      <Text
                        style={[textStyles.body, styles.bdLabel, { color: textMain, fontWeight: "700" }]}
                        numberOfLines={2}
                      >
                        {row.label}
                      </Text>
                    </View>
                    <Text style={[textStyles.body, styles.bdCellHash, { color: textMain }]}>
                      {txFmt}
                    </Text>
                    <Text style={[textStyles.body, styles.bdCellDollar, { color: textMain }]}>
                      {spendFmt}
                    </Text>
                  </View>
                )
              })}
            </View>
          </View>
        ) : null}
      </View>
    </View>
  )
}

const ViewAllTransactionsButton = () => {
  const theme = useContext(ThemeContext).theme
  const { primary } = theme.colors

  return (
    <Link
      action={CommonActions.navigate({
        name: "TransactionsStack",
        params:{
          screen: "TransactionsHome",
          pop: true
        },
      })}
      href={"/transactions"}
      onPress={() => {
        Haptics.selection()
      }}
    >
      <Text style={[textStyles.h4, { color: primary }]}>
        View All Transactions
      </Text>  
    </Link>
  )
}

export default SpendingImpact

const styles = StyleSheet.create({
  breakdownTable: {
    width: "100%",
    borderWidth: 1,
    borderRadius: 10,
    overflow: "hidden",
  },
  bdHeaderRow: {
    flexDirection: "row",
    alignItems: "flex-end",
    borderBottomWidth: 1,
    paddingBottom: 8,
    paddingHorizontal: 10,
    paddingTop: 4,
  },
  bdHeadCell: {
    fontSize: 11,
    fontWeight: "700",
    textTransform: "none" as const,
  },
  bdHeadValue: {
    flex: 1,
    minWidth: 0,
  },
  bdHeadHash: {
    width: 44,
    textAlign: "right",
    fontVariant: ["tabular-nums"],
  },
  bdHeadDollar: {
    width: 76,
    textAlign: "right",
    fontVariant: ["tabular-nums"],
  },
  bdRow: {
    flexDirection: "row",
    alignItems: "center",
    borderBottomWidth: 1,
    paddingVertical: 10,
    paddingHorizontal: 10,
    gap: 8,
  },
  bdCell: {
    justifyContent: "center",
  },
  bdCellValue: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
  },
  bdLabel: {
    flex: 1,
    minWidth: 0,
  },
  bdCellHash: {
    width: 44,
    textAlign: "right",
    fontWeight: "700",
    fontVariant: ["tabular-nums"],
  },
  bdCellDollar: {
    width: 76,
    textAlign: "right",
    fontWeight: "700",
    fontVariant: ["tabular-nums"],
  },
  infoRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  flatListContainer: {
    gap: 10,
    paddingHorizontal: 5,
    paddingVertical: 10
  },
  transactionsContainer: {
    gap: 10
  },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    flexWrap: "wrap",
    columnGap: 10,
    rowGap: 10
  },
  summaryCard: {
    flexDirection: "column",
    justifyContent: "space-between",
    gap: 5,
    borderRadius: 10,
    padding: 15,
  },
  card: {
    borderRadius: 10,
    flexDirection: "column",
    padding: 16
  },
  ellipsis: {
    position: "absolute",
    right: 10,
    top: 10,
    padding: 3
  },
  iconWrapper: {
    position: 'absolute',
    right: 10,
    top: 10,
    zIndex: 1,
    cursor: "pointer",
  },
  infoContainer: {
    gap: 10,
    justifyContent: "space-between",
  },
  infoPair: {},
  optionWindow: {
    borderWidth: 1,
    minWidth: 120,
    position: "absolute",
    right: 16,
    top: 50,
    paddingVertical: 8,
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 10
  },
  cardOption: {
    width: "100%",
    paddingVertical: 9,
    paddingHorizontal: 16,
    flexDirection: "row",
    alignItems: "center",
    gap: 6
  },
})